import {
  AfterViewInit,
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  Optional,
} from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: 'ngx-intl-tel-input[appTelPasteFix]',
})
export class TelPasteFixDirective implements AfterViewInit, OnDestroy {
  private inputEl?: HTMLInputElement;
  private cleanup: Array<() => void> = [];

  constructor(
    private host: ElementRef<HTMLElement>,
    private zone: NgZone,
    @Optional() private ngControl: NgControl
  ) {}

  ngAfterViewInit(): void {
    this.inputEl = this.host.nativeElement.querySelector('input');
    if (!this.inputEl) return;

    this.zone.runOutsideAngular(() => {
      const onBeforeInput = (e: InputEvent) => {
        if (e.inputType !== 'insertFromPaste') return;

        const data = (e as any).data as string | null;

        if (data && data.length) {
          e.preventDefault();
          this.applyCleanValue(data);
        } else {
          setTimeout(() => {
            const v = this.inputEl?.value ?? '';
            this.zone.run(() => this.setControlValue(v));
          }, 0);
        }
      };

      const onPaste = (e: ClipboardEvent) => {
        const text = e.clipboardData?.getData('text') ?? '';
        if (!text) return;

        e.preventDefault();
        this.applyCleanValue(text);
      };

      const onKeyDown = async (e: KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'v') {
          try {
            if (navigator.clipboard?.readText) {
              const text = await navigator.clipboard.readText();
              if (text) {
                e.preventDefault();
                this.applyCleanValue(text);
                return;
              }
            }
          } catch {
          }

          setTimeout(() => {
            const v = this.inputEl?.value ?? '';
            this.zone.run(() => this.setControlValue(v));
          }, 0);
        }
      };

      this.inputEl!.addEventListener('beforeinput', onBeforeInput as EventListener);
      this.inputEl!.addEventListener('paste', onPaste);
      this.inputEl!.addEventListener('keydown', onKeyDown);

      this.cleanup.push(() =>
        this.inputEl!.removeEventListener('beforeinput', onBeforeInput as EventListener)
      );
      this.cleanup.push(() =>
        this.inputEl!.removeEventListener('paste', onPaste)
      );
      this.cleanup.push(() =>
        this.inputEl!.removeEventListener('keydown', onKeyDown)
      );
    });
  }

  private applyCleanValue(raw: string) {
    const cleaned = raw.replace(/[^\d+]/g, '');
    if (!this.inputEl) return;

    this.inputEl.value = cleaned;

    this.inputEl.dispatchEvent(new Event('input', { bubbles: true }));
    this.inputEl.dispatchEvent(new Event('change', { bubbles: true }));

    this.zone.run(() => this.setControlValue(cleaned));
  }

  private setControlValue(v: string) {
    const control = this.ngControl?.control;
    if (!control) return;

    control.setValue(v, { emitEvent: true });
    control.markAsDirty();
    control.markAsTouched();
  }

  ngOnDestroy(): void {
    this.cleanup.forEach(fn => fn());
    this.cleanup = [];
  }
}
